import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LegendItem {
  color: string;
  hexColor: string;
  value: number | string;
  description: string;
  label?: string;
}

export enum ViewModeType {
  THICKNESS = 'thickness',
  MATERIALS = 'materials',
  REGIONS = 'regions',
  RISK = 'risk',
  DEGRADATION = 'degradation',
  REMAINING_LIFE = 'remainingLife',
  CORROSION_RATE = 'corrosionRate',
  CORROSION_RISK_INDEX = 'corrosionRiskIndex',
  STRUCTURE_4D = 'structure4D',
  TYPICAL_DETAILS_FAMILY = 'typicalDetailsFamily',
  DEFAULT = 'default'
}

export interface LegendData {
  viewMode: ViewModeType;
  title: string;
  items: LegendItem[];
  additionalInfo?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SubtitleViewmodeService {
  private legendDataSubject = new BehaviorSubject<LegendData | null>(null);
  public legendData$: Observable<LegendData | null> = this.legendDataSubject.asObservable();

  constructor() { }

  public updateLegendData(legendData: LegendData): void {
    this.legendDataSubject.next(legendData);
  }

  public clearLegendData(): void {
    this.legendDataSubject.next(null);
  }

  public getCurrentLegendData(): LegendData | null {
    return this.legendDataSubject.value;
  }

  public updateThicknessLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.THICKNESS,
      title: 'thickness (mm)',
      items: items.sort((a, b) => (a.value as number) - (b.value as number)),
      additionalInfo: `${items.length} thicknesses`
    };
    this.updateLegendData(legendData);
  }

  public updateMaterialsLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.MATERIALS,
      title: 'material type',
      items: items.sort((a, b) => (a.description || '').localeCompare(b.description || '')),
      additionalInfo: `${items.length} material types`
    };
    this.updateLegendData(legendData);
  }

  public updateRegionsLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.REGIONS,
        title: 'regions',
      items: items.sort((a, b) => (a.description || '').localeCompare(b.description || '')),
      additionalInfo: `${items.length} regions`
    };
    this.updateLegendData(legendData);
  }

  public updateRiskLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.RISK,
      title: 'risk levels',
      items: items.sort((a, b) => (a.value as number) - (b.value as number)),
      additionalInfo: `${items.length} risk levels`
    };
    this.updateLegendData(legendData);
  }

  public updateDegradationLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.DEGRADATION,
      title: 'degradation levels',
      items: items,
      additionalInfo: `${items.length} degradation levels`
    };
    this.updateLegendData(legendData);
  }

  public updateRemainingLifeLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.REMAINING_LIFE,
      title: 'remaining life levels',
      items: items,
      additionalInfo: `${items.length} remaining life levels`
    };
    this.updateLegendData(legendData);
  }

  public updateCorrosionRateLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.CORROSION_RATE,
      title: 'corrosion rate levels',
      items: items,
      additionalInfo: `${items.length} corrosion rate levels`
    };
    this.updateLegendData(legendData);
  }

  public updateCorrosionRiskIndexLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.CORROSION_RISK_INDEX,
      title: 'corrosion risk index levels',
      items: items,
      additionalInfo: `${items.length} corrosion risk index levels`
    };
    this.updateLegendData(legendData);
  }

  public updateStructure4DLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.STRUCTURE_4D,
      title: '4D Structure',
      items: items,
      additionalInfo: `${items.length} dates`
    };
    this.updateLegendData(legendData);
  }

  public updateTypicalDetailsFamilyLegend(items: LegendItem[]): void {
    const legendData: LegendData = {
      viewMode: ViewModeType.TYPICAL_DETAILS_FAMILY,
      title: 'typical details family',
      items: items.sort((a, b) => (a.description || '').localeCompare(b.description || '')),
      additionalInfo: `${items.length} families`
    };
    this.updateLegendData(legendData);
  }
} 
